import { FC, useEffect, useState } from "react";
import { Modal, Button, Input } from "rizzui";
import { ProviderInvoiceService } from "../../../services/providerInvoices/providerInvoiceService";
import { toast } from "sonner";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import CustomSelect from "../../../components/forms/CustomSelect";
import { DatePicker } from "../../../components/forms/DatePicker";
import moment from "moment";
import { es } from "date-fns/locale";

interface ModalQuarterlyBillingProps {
    isOpen: boolean;
    onClose: () => void;
}

const ModalQuarterlyBilling: FC<ModalQuarterlyBillingProps> = ({ isOpen, onClose }) => {
    
    // STATES

    const [dateFrom, setDateFrom] = useState<Date | null>(new Date(new Date().getFullYear(), 0, 1));
    const [dateTo, setDateTo] = useState<Date | null>(new Date(new Date().getFullYear(), 2, 31));
    const [invoiceCount, setInvoiceCount] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [company, setCompany] = useState<string>('');
    const [isSearched, setIsSearched] = useState(false);

    // HOOKS

    const service = new ProviderInvoiceService();
    const { userCan } = usePrivilege();
    const { getCompaniesList } = useCompanies();

    // FUNCTIONS

    // ----------------------------------------------------------------------------------------------------------
    /**
     * @ES EJECUTAR FACTURACIÓN TRIMESTRAL PARA UN AÑO
     * @EN EXECUTE QUARTERLY BILLING FOR A YEAR AUTOMATICALLY
     */
    // ----------------------------------------------------------------------------------------------------------    
    const handleRun = async () => {
        try {
            if (userCan('list', 'company') && (!company || company === '')) {
                toast.error('La compañía es obligatoria');
                return;
            }
            if (!dateFrom || !dateTo) {
                toast.error('Selecciona el rango de fechas');
                return;
            }
            if (moment(dateTo).isBefore(moment(dateFrom), 'day')) {
                toast.error('La fecha de fin no puede ser anterior a la fecha de inicio');
                return;
            }
            const parsedInvoiceCount = Number(invoiceCount);
            if (!invoiceCount || Number.isNaN(parsedInvoiceCount) || parsedInvoiceCount < 1) {
                toast.error('Indica un número válido de facturas (mínimo 1)');
                return;
            }

            setIsLoading(true);
            const response = await service.generateAutomaticInvoices({
                company: company || undefined,
                dateFrom: moment(dateFrom).format('YYYY-MM-DD'),
                dateTo: moment(dateTo).format('YYYY-MM-DD'),
                invoiceCount: parsedInvoiceCount,
            });
            const payload = response.getResponseData();
            if (payload?.success) {
                toast.success(payload.message);
                window.dispatchEvent(new Event('provider-invoices:refresh'));
                onClose();
            } else {
                toast.error(payload?.message || 'No se pudo ejecutar la facturación automática');
            }
        } catch (error: any) {
            toast.error(error?.message || 'No se pudo ejecutar la facturación automática');
        } finally {
            setIsLoading(false);
        }
    };
    // ----------------------------------------------------------------------------------------------------------
    
    // USE EFFECT

    useEffect(() => {
        if (isOpen) {
            setDateFrom(new Date(new Date().getFullYear(), 0, 1));
            setDateTo(new Date(new Date().getFullYear(), 2, 31));
            setInvoiceCount('');
            setCompany('');
            setIsSearched(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (!userCan('list', 'company')) return;
        if (company || isSearched) return;
        if (getCompaniesList().length === 0) return;

        setIsSearched(true);
        const defaultCompany = getCompaniesList()?.find((g: any) => g.label === 'GESTION DE RESIDUOS 2023 SLU');
        setCompany(defaultCompany?.value ?? '');
    }, [isOpen, userCan, company, isSearched, getCompaniesList]);

    // RENDER

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="sm"
            overlayClassName="backdrop-blur"
            containerClassName='!shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-8'>
                <div className='mb-7 flex items-center justify-between'>
                    <h2 className="text-xl font-semibold">Facturación automática</h2>
                </div>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="date-from" className="block text-sm font-medium text-gray-700 mb-2">
                            Fecha inicio <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                            id="date-from"
                            monthsShown={1}
                            placeholderText="Selecciona fecha de inicio"
                            inputProps={{
                                inputClassName: 'h-9 [&_input]:text-ellipsis bg-white',
                            }}
                            className="w-full"
                            selected={dateFrom}
                            onChange={(d: Date | null) => setDateFrom(d)}
                            locale={es}
                        />
                    </div>
                    <div>
                        <label htmlFor="date-to" className="block text-sm font-medium text-gray-700 mb-2">
                            Fecha fin <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                            id="date-to"
                            monthsShown={1}
                            placeholderText="Selecciona fecha de fin"
                            inputProps={{
                                inputClassName: 'h-9 [&_input]:text-ellipsis bg-white',
                            }}
                            className="w-full"
                            selected={dateTo}
                            onChange={(d: Date | null) => setDateTo(d)}
                            locale={es}
                            minDate={dateFrom ?? undefined}
                        />
                    </div>

                    <Input
                        type="number"
                        label="Número de facturas"
                        value={invoiceCount}
                        min={1}
                        max={10000}
                        step={1}
                        onChange={(e) => setInvoiceCount(e.target.value)}
                    />

                    <p className="text-xs text-gray-500">
                        Se crearán facturas aleatorias &lt; 1.000€ entre proveedores activos con CIF. Máximo 3 facturas por CIF en esta ejecución.
                    </p>

                    {userCan('list', 'company') && (
                        <CustomSelect
                            required
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            value={
                                company
                                    ? { value: company, label: getCompaniesList()?.find((g: any) => g.value === company)?.label }
                                    : getCompaniesList()?.find((g: any) => g.label === 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            options={getCompaniesList()}
                            onChange={(e: any) => setCompany(e?.value ?? '')}
                            error={!company ? 'La compañía es obligatoria' : ''}
                        />
                    )}

                    <div className="flex gap-2 justify-end pt-2">
                        <Button variant="outline" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button color="secondary" isLoading={isLoading} onClick={handleRun}>
                            Ejecutar
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ModalQuarterlyBilling;
